import React, { useState } from "react";
import { Link } from "react-router-dom";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import PackageCard from "../../components/PackageCard";
import { formatCurrency } from "../../utils/displayHelpers";

/**
 * CHECK MY PACKAGE — WHAT IS MINE, WITHOUT AN ACCOUNT (user, 2026-10-19).
 *
 * ── WHY THIS PAGE EXISTS ────────────────────────────────────────────────────────────────────────
 *
 * A guest buys a package, the confirmation email goes astray, and until now the only way to see what
 * they own was to start a booking and work through "Use my package" — which asks a person who may not
 * want to book anything today to pick a service, a professional and a time first. This page asks for
 * the proof and then just TELLS them: what it is, what is left, and when it runs out.
 *
 * ── READ ONLY, DELIBERATELY ─────────────────────────────────────────────────────────────────────
 *
 * Nothing here books, transfers, freezes or pays. Every one of those moves money or entitlement, and
 * each already lives behind its own confirmation (the booking page, the transfer half, the freeze
 * route, the reactivation checkout). A lookup screen that could also spend a session would be the one
 * place where reading and spending are the same button — so it is not built that way.
 *
 * ── THE PROOF IS THE SAME BAR AS EVERYWHERE ELSE ────────────────────────────────────────────────
 *
 * Email it was bought with + the phone's last 4 digits + the 16-digit package number, then a code to
 * that mailbox. These are the exact factors `/guest/challenge/start` already demands, reused rather
 * than reimplemented: a second, weaker check on a page that lists somebody's purchases would be the
 * weak link whatever the other screens do.
 *
 * ── WHY THE ANSWERS COME FROM THE SERVER ────────────────────────────────────────────────────────
 *
 * Sessions left, expiry, and the reactivation fee are arithmetic over stored counters. Recomputing any
 * of them here would be a second implementation that could disagree with the route that actually
 * charges — the lesson `mayTransfer` and `renewal` were both written to encode.
 */

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const money = (amount, currency) => {
  try {
    return formatCurrency(Number(amount) || 0, currency || "USD");
  } catch (err) {
    return `${currency || "USD"} ${Number(amount || 0).toFixed(2)}`;
  }
};

const dateLabel = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const isPast = (value) => {
  const date = value ? new Date(value) : null;
  return Boolean(date && !Number.isNaN(date.getTime()) && date.getTime() < Date.now());
};

/**
 * THE STATUS, IN WORDS (reported 2026-10-21: *"not revealing the true status/numbers of sessions remaining"*).
 *
 * The page printed the stored value — `pending`, `frozen`, `cancelled` — which is the database's word for what
 * happened, not the customer's. "pending" in particular is dangerous to show as it is: it reads like something
 * waiting for THEM, when it means a checkout that was never paid for.
 */
function statusLabel(status, { expired, finished } = {}) {
  if (expired) return "Expired";
  switch (String(status || "").toLowerCase()) {
    case "completed":
      return "Fully used";
    case "frozen":
      return "Frozen — under review";
    case "cancelled":
      return "Cancelled";
    case "refunded":
      return "Refunded";
    case "expired":
      return "Expired";
    case "pending":
      return "Not paid for — this is not an active package";
    case "active":
      return finished ? "Fully used" : "Active";
    default:
      return status ? String(status) : "Unavailable";
  }
}

/**
 * SESSIONS THE PACKAGE IS HOLDING (22G). They were lost to a late cancellation and are still redeemable, so a
 * figure of "3 left" beside a hold is not the whole truth — without this line the customer cannot tell why the
 * number is what it is, or that two more can be bought back.
 */
function HeldSessions({ purchase, money }) {
  const units = Number(purchase.redeemUnits) || 0;
  if (units <= 0) return null;
  return (
    <p
      style={{
        margin: "8px 0 0",
        fontSize: "12.5px",
        color: "#92400e",
        background: "#fffbeb",
        border: "1px solid #fde68a",
        borderRadius: "8px",
        padding: "8px 10px",
      }}
    >
      <strong>{units}</strong> session{units === 1 ? "" : "s"} held from a late
      cancellation, worth {money(purchase.redeemTotal, purchase.currency)}. They can be
      redeemed when you next book at this shop.
    </p>
  );
}

function Row({ label, value, tone }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: "12px",
        padding: "7px 0",
        borderBottom: "1px solid #eef1f6",
        fontSize: "14px",
      }}
    >
      <span style={{ color: "#5b6475" }}>{label}</span>
      <span
        style={{
          color: tone === "danger" ? "#c0392b" : "#1f2a44",
          fontWeight: 600,
          textAlign: "right",
        }}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * WHERE THE SHOP IS (user, 2026-10-19: *"put the shop's business name with a link to the top of the shop like
 * this /business/mos-beauty-world/"*).
 *
 * The path is `business.slug`, NOT the shop-location slug: `/business/:businessSlug` is the public storefront,
 * and a location slug there would 404. Built here from what the server sends so the page never guesses — a
 * package whose shop has no slug simply renders its name as text rather than a dead link.
 */
function shopPath(purchase) {
  const slug = purchase?.shopId?.businessId?.slug;
  return slug ? `/business/${slug}` : null;
}

const shopLinkStyle = { color: "#2563eb", textDecoration: "underline", fontWeight: 600 };

export default function PackageLookupPage() {
  const [step, setStep] = useState("identity");
  const [identity, setIdentity] = useState({
    email: "",
    phoneLast4: "",
    accountNumber: "",
  });
  const [challengeId, setChallengeId] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const inputStyle = {
    width: "100%",
    padding: "11px 13px",
    borderRadius: "9px",
    border: "1px solid #d6dbe6",
    fontSize: "15px",
    boxSizing: "border-box",
    marginTop: "6px",
  };
  const labelStyle = {
    display: "block",
    fontSize: "13px",
    fontWeight: 600,
    color: "#3b475c",
    marginTop: "14px",
  };

  const startChallenge = async (event) => {
    if (event) event.preventDefault();
    setError("");
    setBusy(true);
    try {
      const response = await fetch(`${API_BASE}/api/packages/guest/challenge/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: identity.email,
          phoneLast4: identity.phoneLast4,
          accountNumber: identity.accountNumber,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.message || "We could not match those details.");
        return;
      }
      // A match whose email never left the building must not pretend a code is coming.
      if (data.emailDelivered === false) {
        setError(
          data.message ||
            "We found your package but couldn't send the code. Please try again.",
        );
        return;
      }
      setChallengeId(data.challengeId);
      setStep("code");
    } catch (err) {
      setError("Unable to reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const verifyChallenge = async (event) => {
    if (event) event.preventDefault();
    setError("");
    setBusy(true);
    try {
      const verified = await fetch(`${API_BASE}/api/packages/guest/challenge/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challengeId, otpCode: code }),
      });
      const verifiedData = await verified.json();
      if (!verified.ok) {
        setError(verifiedData.message || "Incorrect or expired code.");
        return;
      }

      const token = verifiedData.guestAccessToken;
      const listed = await fetch(`${API_BASE}/api/packages/guest/purchases`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const listedData = await listed.json();
      if (!listed.ok) {
        setError(listedData.message || "We could not load your packages.");
        return;
      }

      setResult({
        purchases: listedData.purchases || [],
        usedUpPurchases: listedData.usedUpPurchases || [],
      });
      setStep("result");
    } catch (err) {
      setError("Unable to reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const restart = () => {
    setStep("identity");
    setCode("");
    setChallengeId("");
    setError("");
    setResult(null);
  };

  const usable = result?.purchases || [];
  const notUsable = result?.usedUpPurchases || [];
  const empty = step === "result" && usable.length === 0 && notUsable.length === 0;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f7f8fb",
        /*
         * CLEARING THE FIXED HEADER (found in the browser, 2026-10-19: *"the package lookup is merged together
         * at the top"*). `CustomerHeader` is `position: fixed`, so it takes no space in the flow and the demo
         * banner above it takes none either — without this offset the title started at y=0 and sat underneath
         * the navigation. Same arithmetic as the transfer page: the banner publishes its own height, the header
         * is 50px tall (56px narrow), and 56 is used here so the narrow case cannot clip.
         */
        paddingTop: "calc(var(--demo-banner-height, 0px) + 56px)",
      }}
    >
      <CustomerHeader />
      <main
        style={{
          maxWidth: "760px",
          margin: "0 auto",
          padding: "32px 20px 56px",
          fontFamily: "Inter, sans-serif",
        }}
      >
        <h1 style={{ fontSize: "26px", color: "#1f2a44", margin: "0 0 6px" }}>
          Check my package
        </h1>
        <p style={{ color: "#5b6475", margin: "0 0 22px", fontSize: "15px" }}>
          Look up a package you bought. This page only shows what you own — to use a
          session, book from the shop's page and choose “Use my package”.
        </p>

        {step === "identity" && (
          <form
            onSubmit={startChallenge}
            style={{
              background: "#ffffff",
              borderRadius: "14px",
              padding: "22px",
              boxShadow: "0 1px 3px rgba(16,24,40,0.08)",
            }}
          >
            <label style={labelStyle}>
              Email used to buy the package
              <input
                type="email"
                value={identity.email}
                onChange={(e) =>
                  setIdentity({ ...identity, email: e.target.value })
                }
                required
                autoComplete="email"
                style={inputStyle}
              />
            </label>

            <label style={labelStyle}>
              Last 4 digits of your phone
              <input
                inputMode="numeric"
                maxLength={4}
                value={identity.phoneLast4}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
                  setIdentity({ ...identity, phoneLast4: digits });
                }}
                required
                style={inputStyle}
              />
            </label>

            <label style={labelStyle}>
              16-digit package number
              <input
                inputMode="numeric"
                placeholder="0000 0000 0000 0000"
                value={identity.accountNumber}
                onChange={(e) =>
                  setIdentity({ ...identity, accountNumber: e.target.value })
                }
                required
                style={inputStyle}
              />
              <span
                style={{
                  display: "block",
                  fontSize: "12px",
                  color: "#7a8496",
                  marginTop: "6px",
                  fontWeight: 400,
                }}
              >
                On your purchase email, and on the card in your account.
              </span>
            </label>

            {error && (
              <p style={{ color: "#c0392b", fontSize: "14px", marginTop: "14px" }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={busy}
              style={{
                marginTop: "20px",
                width: "100%",
                padding: "13px",
                borderRadius: "9px",
                border: "none",
                background: busy ? "#9aa7bd" : "#1f2a44",
                color: "#ffffff",
                fontSize: "15px",
                fontWeight: 700,
                cursor: busy ? "default" : "pointer",
              }}
            >
              {busy ? "Checking…" : "Send me a code"}
            </button>
          </form>
        )}

        {step === "code" && (
          <form
            onSubmit={verifyChallenge}
            style={{
              background: "#ffffff",
              borderRadius: "14px",
              padding: "22px",
              boxShadow: "0 1px 3px rgba(16,24,40,0.08)",
            }}
          >
            <p style={{ marginTop: 0, color: "#3b475c", fontSize: "14px" }}>
              We sent a 6-digit code to the email on this package. It expires in 10
              minutes.
            </p>
            <label style={labelStyle}>
              Code
              <input
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                required
                autoFocus
                style={{ ...inputStyle, letterSpacing: "6px", fontSize: "19px" }}
              />
            </label>

            {error && (
              <p style={{ color: "#c0392b", fontSize: "14px", marginTop: "14px" }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={busy}
              style={{
                marginTop: "20px",
                width: "100%",
                padding: "13px",
                borderRadius: "9px",
                border: "none",
                background: busy ? "#9aa7bd" : "#1f2a44",
                color: "#ffffff",
                fontSize: "15px",
                fontWeight: 700,
                cursor: busy ? "default" : "pointer",
              }}
            >
              {busy ? "Checking…" : "Show my packages"}
            </button>

            {/* A typo in the email used to be a dead end on the booking page; it must not be one here. */}
            <button
              type="button"
              onClick={() => {
                setStep("identity");
                setCode("");
                setError("");
              }}
              style={{
                marginTop: "10px",
                width: "100%",
                padding: "10px",
                borderRadius: "9px",
                border: "1px solid #d6dbe6",
                background: "#ffffff",
                color: "#3b475c",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Wrong email? Go back
            </button>
          </form>
        )}

        {step === "result" && (
          <div>
            {empty && (
              <div
                style={{
                  background: "#ffffff",
                  borderRadius: "14px",
                  padding: "22px",
                  boxShadow: "0 1px 3px rgba(16,24,40,0.08)",
                }}
              >
                <p style={{ margin: 0, color: "#3b475c" }}>
                  We verified you, but there are no packages under this email address.
                </p>
              </div>
            )}

            {usable.length > 0 && (
              <>
                <h2 style={{ fontSize: "18px", color: "#1f2a44", margin: "0 0 12px" }}>
                  Ready to use
                </h2>
                {usable.map((purchase) => {
                  const remaining = Number(
                    purchase.sessionsRemaining ??
                      (Number(purchase.totalSessions) || 0) -
                        (Number(purchase.sessionsUsed) || 0),
                  );
                  const expired = isPast(purchase.validUntil);
                  const shopName = purchase.shopId?.name;
                  const toShop = shopPath(purchase);
                  return (
                    <div
                      key={purchase._id}
                      style={{
                        display: "flex",
                        gap: "20px",
                        flexWrap: "wrap",
                        alignItems: "flex-start",
                        background: "#ffffff",
                        borderRadius: "14px",
                        padding: "18px",
                        marginBottom: "14px",
                        boxShadow: "0 1px 3px rgba(16,24,40,0.08)",
                      }}
                    >
                      <PackageCard
                        accountNumber={purchase.accountNumber}
                        holderName={purchase.customerName}
                        validUntil={purchase.validUntil}
                        totalSessions={purchase.totalSessions}
                        shopName={purchase.shopId?.name}
                        packageName={purchase.packageSnapshot?.name}
                        status={purchase.status}
                      />
                      <div style={{ flex: "1 1 240px", minWidth: "220px" }}>
                        <h3 style={{ margin: "0 0 8px", fontSize: "16px", color: "#1f2a44" }}>
                          {purchase.packageSnapshot?.name || "Package"}
                        </h3>
                        <Row
                          label="Shop"
                          value={
                            shopName ? (
                              toShop ? (
                                <Link to={toShop} style={shopLinkStyle}>
                                  {shopName}
                                </Link>
                              ) : (
                                shopName
                              )
                            ) : (
                              "—"
                            )
                          }
                        />
                        <Row
                          label="Sessions left"
                          value={`${remaining} of ${
                            Number(purchase.totalSessions) || 0
                          }`}
                        />
                        <Row
                          label="Valid until"
                          value={dateLabel(purchase.validUntil) || "—"}
                          tone={expired ? "danger" : undefined}
                        />
                        <HeldSessions purchase={purchase} money={money} />
                        {purchase.renewal?.canReactivate && (
                          <p
                            style={{
                              marginTop: "10px",
                              fontSize: "13px",
                              color: "#8a6d1f",
                              background: "#fff8e1",
                              borderRadius: "8px",
                              padding: "9px 11px",
                            }}
                          >
                            Expired — reactivation opens for{" "}
                            {purchase.renewal.windowDays} days for a{" "}
                            {money(purchase.renewal.fee, purchase.renewal.currency)}{" "}
                            fee. Reactivate from your account, or when you next book.
                          </p>
                        )}
                        <p
                          style={{
                            margin: "10px 0 0",
                            fontSize: "12px",
                            color: "#7a8496",
                          }}
                        >
                          To spend a session, book a service at{" "}
                          {shopName && toShop ? (
                            <Link to={toShop} style={shopLinkStyle}>
                              {shopName}
                            </Link>
                          ) : (
                            "this shop"
                          )}{" "}
                          and choose “Use my package”.
                        </p>
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {notUsable.length > 0 && (
              <>
                <h2
                  style={{
                    fontSize: "18px",
                    color: "#1f2a44",
                    margin: "22px 0 12px",
                  }}
                >
                  Not usable right now
                </h2>
                {notUsable.map((purchase) => {
                  const expired = isPast(purchase.validUntil);
                  const finished =
                    !expired &&
                    (Number(purchase.sessionsUsed) || 0) >=
                      (Number(purchase.totalSessions) || 0);
                  return (
                    <div
                      key={purchase.id || purchase._id}
                      style={{
                        background: "#ffffff",
                        borderRadius: "14px",
                        padding: "18px",
                        marginBottom: "14px",
                        boxShadow: "0 1px 3px rgba(16,24,40,0.08)",
                      }}
                    >
                      <h3
                        style={{
                          margin: "0 0 8px",
                          fontSize: "16px",
                          color: "#1f2a44",
                        }}
                      >
                        {purchase.name || "Package"}
                      </h3>
                      <Row
                        label="Sessions used"
                        value={`${Number(purchase.sessionsUsed) || 0} of ${
                          Number(purchase.totalSessions) || 0
                        }`}
                      />
                      {/* THE COMPANION FIGURE, AND THE ONE THE CUSTOMER ACTUALLY ASKS ABOUT. "3 of 3 used"
                          leaves them dividing; "0 left" answers it. Both are derived on the server, from the
                          same counters the redemption guard reads. */}
                      <Row
                        label="Sessions left"
                        value={`${Number(purchase.sessionsRemaining) || 0} of ${
                          Number(purchase.totalSessions) || 0
                        }`}
                        tone={expired ? "danger" : undefined}
                      />
                      <Row
                        label="Valid until"
                        value={dateLabel(purchase.validUntil) || "—"}
                        tone={expired ? "danger" : undefined}
                      />
                      <Row
                        label="Status"
                        value={statusLabel(purchase.status, { expired, finished })}
                        tone={expired ? "danger" : undefined}
                      />

                      {purchase.renewal?.canReactivate && (
                        <p
                          style={{
                            marginTop: "10px",
                            fontSize: "13px",
                            color: "#8a6d1f",
                            background: "#fff8e1",
                            borderRadius: "8px",
                            padding: "9px 11px",
                          }}
                        >
                          {purchase.renewal.undeliveredSessions} unused session
                          {purchase.renewal.undeliveredSessions === 1 ? "" : "s"} can
                          be recovered for {purchase.renewal.windowDays} more days
                          for a {money(purchase.renewal.fee, purchase.renewal.currency)}{" "}
                          fee.
                        </p>
                      )}

                      {/* A hold is sessions a late cancellation lost, still buyable back. It is stated,
                          not offered — this page takes no payments. */}
                      {Number(purchase.redeemUnits) > 0 && (
                        <p
                          style={{
                            marginTop: "8px",
                            fontSize: "13px",
                            color: "#3b475c",
                          }}
                        >
                          {purchase.redeemUnits} held session
                          {purchase.redeemUnits === 1 ? "" : "s"} (
                          {money(purchase.redeemTotal, purchase.currency)}) can be
                          restored when you next book.
                        </p>
                      )}
                    </div>
                  );
                })}
              </>
            )}

            <button
              type="button"
              onClick={restart}
              style={{
                marginTop: "8px",
                padding: "11px 18px",
                borderRadius: "9px",
                border: "1px solid #d6dbe6",
                background: "#ffffff",
                color: "#3b475c",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Look up another package
            </button>
          </div>
        )}

        <p style={{ marginTop: "22px", fontSize: "13px", color: "#7a8496" }}>
          Bought with an account? Your packages are always listed in{" "}
          <Link to="/customer/dashboard" style={{ color: "#1f2a44" }}>
            your dashboard
          </Link>
          .
        </p>
      </main>
      <CustomerFooter />
    </div>
  );
}
